/**
 * device-token — this device's own bearer, when the person using it has one.
 *
 * The pilot bakes ONE bearer into the bundle at build time, which means every
 * install is the same actor with the same project scope. That is fine for one
 * superintendent and wrong for four: Red's V0 RBAC requirement is two people
 * sharing a project and two isolated to their own (2026-07-30).
 *
 * The coordinator side already resolves a per-person token to that person's own
 * actor_id and project_ids (pilot.py `actors`). This is the device half: store
 * the code someone was issued, and prefer it over the baked one.
 *
 * WHY A SYNCHRONOUS CACHE. The transport builds its config synchronously
 * (`transportConfigFromEnv`) and is constructed on paths that cannot await. So
 * the token is read from IndexedDB ONCE at boot into a module-level value, and
 * every later read is sync. `initDeviceToken()` must therefore be awaited before
 * the first send — `runtime.ts` owns that ordering.
 *
 * WHY THE BAKED TOKEN STAYS. Removing it would log out every phone in the field
 * the moment this ships. Keeping it as the fallback is what lets people move onto
 * their own codes one at a time, which is the same migration property the
 * coordinator's roster and rotation window both have.
 *
 * NOT A PASSWORD. This is a bearer: possession is the whole proof. It is stored
 * unencrypted in IndexedDB because the device has nowhere better — the same is
 * already true of the baked token, which anyone can read out of the bundle. Real
 * per-user authentication is a later change (see the runbook); this makes four
 * people distinguishable and correctly scoped, which is what V0 needs.
 */

import { getMeta, setMeta } from "../outbox/db";

/** IndexedDB `meta` key. Namespaced so it cannot collide with device config. */
export const DEVICE_TOKEN_KEY = "identity.device_token";

/** Populated by initDeviceToken(); null means "use the baked bearer". */
let cached: string | null = null;
let loaded = false;

/** Read the stored token into the sync cache. Call once, at boot, before the
 * dispatcher can send. Safe to call again — it simply re-reads. */
export async function initDeviceToken(): Promise<string | null> {
  try {
    const v = await getMeta(DEVICE_TOKEN_KEY);
    cached = typeof v === "string" && v.trim() !== "" ? v.trim() : null;
  } catch {
    // A storage failure must not stop the app booting: the baked bearer still
    // works, so the worst case is the old shared identity rather than no app.
    cached = null;
  }
  loaded = true;
  return cached;
}

/** The device's token, or null to fall back to the baked bearer.
 *
 * Returns null before initDeviceToken() has run rather than throwing: a caller
 * that races the boot gets the baked token, which is the pre-existing behaviour,
 * not an error. */
export function deviceToken(): string | null {
  return loaded ? cached : null;
}

/** True once a person has entered their own code on this device. */
export function hasDeviceToken(): boolean {
  return deviceToken() !== null;
}

/** Store a code and make it effective immediately.
 *
 * Trimmed because these are read off a screen or a slip of paper and a trailing
 * space is invisible — and a bearer is compared byte-for-byte server-side, so an
 * invisible space is an authentication failure with no clue as to why. */
export async function setDeviceToken(token: string): Promise<void> {
  const t = token.trim();
  if (t === "") throw new Error("device-token: refusing to store an empty code");
  await setMeta(DEVICE_TOKEN_KEY, t);
  cached = t;
  loaded = true;
}

/** Forget this device's code and fall back to the baked bearer. */
export async function clearDeviceToken(): Promise<void> {
  await setMeta(DEVICE_TOKEN_KEY, "");
  cached = null;
  loaded = true;
}

/**
 * SUPERSEDED BY WORKOS COOKIE AUTH (2026-08-07). The device-code RBAC scheme
 * below (a per-device bearer standing in for identity) is dead: identity is now
 * the sealed WorkOS session cookie, resolved server-side to actor_id +
 * project_ids via the signed roster, and every client sends it with
 * `credentials: "include"`. The launch gate that demanded a device code fired
 * AFTER WorkOS had already authed the user — a second, obsolete code wall — so it
 * is removed. `needsCode()` is retained as a permanent `false` so any lingering
 * caller is inert rather than a compile break; the DeviceCodePanel and its mount
 * are gone. The bearer-token storage helpers remain only as inert plumbing behind
 * the transport's `token` fallback (empty in cookie mode; the cookie authenticates).
 */
export function needsCode(): boolean {
  return false;
}

/** Test-only reset of the sync cache (mirrors db.ts's _reset*ForTests). */
export function _resetDeviceTokenForTests(): void {
  cached = null;
  loaded = false;
}
